
import { createSlice, createAsyncThunk  } from '@reduxjs/toolkit' 
import axios from 'axios' 
import { STATUS } from '../enums'
import { PrayselyError, config, baseUrl } from '../constants';  
import notifService from './notifService'
 
 

const initialState = { 
    allNotifications: [], 
    newMessageNotifications: [],
    marketplaceNotifications: [],   
    getNotificationsStatus: STATUS.NOT_STARTED,
    bulkActionStatus: STATUS.NOT_STARTED,
    nextCursor: null,
    unreadCount: 0,
}


export const getNotifications = createAsyncThunk('notifications/getNotifications', async (data, thunkAPI) => {
    try { 
      const token = thunkAPI.getState().auth.user?.token || thunkAPI.getState().auth.token
      const response = await axios.post(`${baseUrl}/api/notifications/getAllNotifications`, data, config(token))

      return {
        ...response.data,
        markRead: data?.markRead
      }
    }
    catch (error) {
      const errorMessage = PrayselyError(error)
      return thunkAPI.rejectWithValue(errorMessage)
    }
});

export const markAllNotificationsRead = createAsyncThunk('notifications/markAllRead', async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user?.token || thunkAPI.getState().auth.token
      const response = await axios.post(`${baseUrl}/api/notifications/getAllNotifications`, { markRead: true }, config(token))

      return response.data
    }
    catch (error) {
      const errorMessage = PrayselyError(error)
      return thunkAPI.rejectWithValue(errorMessage)
    }
})

export const deleteAllNotifications = createAsyncThunk('notifications/deleteAll', async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user?.token || thunkAPI.getState().auth.token
      return await notifService.deleteAllNotifs(token)
    }
    catch (error) {
      const errorMessage = PrayselyError(error)
      return thunkAPI.rejectWithValue(errorMessage)
    }
})

const syncNotificationGroups = (state) => {
  state.newMessageNotifications = state.allNotifications.filter(
    (n) => n?.topic === 'app.message.new' && !n.readAt
  )

  state.marketplaceNotifications = state.allNotifications.filter(
    (n) => typeof n?.topic === 'string' && n.topic.startsWith('marketplace.') && !n.readAt
  )
}

export const notificationSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {  
    // use this when user logs out or you want a hard reset
        resetNotifications(state) {
          state.allNotifications = []
          state.newMessageNotifications = []
          state.marketplaceNotifications = []
          state.nextCursor = null
          state.unreadCount = 0
          state.getNotificationsStatus = STATUS.NOT_STARTED
          state.bulkActionStatus = STATUS.NOT_STARTED
        },
        removeNewMessageNotification(state, action) {
            const notificationIdToRemove = action.payload
            state.newMessageNotifications = state.newMessageNotifications.filter(n => n._id !== notificationIdToRemove)
            // state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
    },
    extraReducers: (builder) => {
        builder 
            .addCase(getNotifications.pending, (state) => {
                state.getNotificationsStatus = STATUS.LOADING
            })
            .addCase(getNotifications.fulfilled, (state, action ) => {
                state.getNotificationsStatus = STATUS.SUCCESS   
                const items = action.payload?.items ?? []
                const nextCursor = action.payload?.nextCursor ?? null
                const unreadCount = Number(action.payload?.unreadCount ?? 0)

                // Append without duplicates (cursor pagination)
                const existingIds = new Set(state.allNotifications.map((n) => String(n._id)))
                const newItems = items.filter((n) => !existingIds.has(String(n._id)))
                state.allNotifications = [...state.allNotifications, ...newItems]

                state.nextCursor = nextCursor
                state.unreadCount = unreadCount

                // Split for UI convenience by topic prefix
                if(action.payload?.markRead){ 
                    const readAt = new Date().toISOString()
                    state.allNotifications = state.allNotifications.map((notification) => ({
                        ...notification,
                        readAt: notification.readAt || readAt
                    }))
                    state.unreadCount = 0
                }

                syncNotificationGroups(state)

            })
            .addCase(getNotifications.rejected, (state) => {
                state.getNotificationsStatus = STATUS.ERROR
            })
            .addCase(markAllNotificationsRead.pending, (state) => {
                state.bulkActionStatus = STATUS.LOADING
            })
            .addCase(markAllNotificationsRead.fulfilled, (state) => {
                const readAt = new Date().toISOString()
                state.bulkActionStatus = STATUS.SUCCESS
                state.unreadCount = 0
                state.allNotifications = state.allNotifications.map((notification) => ({
                    ...notification,
                    readAt: notification.readAt || readAt
                }))
                syncNotificationGroups(state)
            })
            .addCase(markAllNotificationsRead.rejected, (state) => {
                state.bulkActionStatus = STATUS.ERROR
            })
            .addCase(deleteAllNotifications.pending, (state) => {
                state.bulkActionStatus = STATUS.LOADING
            })
            .addCase(deleteAllNotifications.fulfilled, (state) => {
                state.bulkActionStatus = STATUS.SUCCESS
                state.allNotifications = []
                state.newMessageNotifications = []
                state.marketplaceNotifications = []
                state.nextCursor = null
                state.unreadCount = 0
            })
            .addCase(deleteAllNotifications.rejected, (state) => {
                state.bulkActionStatus = STATUS.ERROR
            }) 
    }
})

export const { 
    // add exported actions here if needed
} = notificationSlice.actions
 
export default notificationSlice.reducer
